<template>
  <div class="list-page-container">
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-chat-line-square"></i>
          <span>管理客户投诉和咨询记录，跟踪处理进度</span>
        </div>
        <div class="action-wrapper">
          <el-button type="primary" @click="handleAdd" v-if="hasPermission('cus:consultation:save')">
            <i class="el-icon-plus"></i> 新建客诉
          </el-button>
        </div>
      </div>
    </div>

    <div class="control-panel has-query">
      <div class="control-container">
        <el-form :inline="true" :model="searchForm" class="filter-form" @submit.native.prevent>
          <el-form-item>
            <el-input v-model="searchForm.title" placeholder="客诉标题" clearable class="filter-input" @keyup.enter.native="handleSearch">
              <i slot="prefix" class="el-input__icon el-icon-search"></i>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-select v-model="searchForm.followStatus" placeholder="跟进状态" clearable class="filter-select" @change="handleSearch">
              <el-option label="未处理" value="PENDING"></el-option>
              <el-option label="处理中" value="IN_PROGRESS"></el-option>
              <el-option label="已解决" value="RESOLVED"></el-option>
              <el-option label="已关闭" value="CLOSED"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-select v-model="searchForm.complaintType" placeholder="客诉类型" clearable class="filter-select" @change="handleSearch">
              <el-option v-for="item in complaintTypeList" :key="item.key" :label="item.name" :value="item.key"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-date-picker v-model="searchForm.dateRange" type="daterange" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" value-format="yyyy-MM-dd" class="filter-date" @change="handleSearch"></el-date-picker>
          </el-form-item>
          <el-form-item>
            <div class="search-btns">
              <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
              <el-button @click="handleReset" icon="el-icon-refresh">重置</el-button>
            </div>
          </el-form-item>
        </el-form>
      </div>
    </div>

    <div class="statistics-panel" v-if="statistics">
      <div class="stat-card" v-for="(item, key) in statCards" :key="key" :class="item.class">
        <div class="stat-value">{{ statistics[key] || 0 }}</div>
        <div class="stat-label">{{ item.label }}</div>
      </div>
    </div>

    <el-card class="table-card" shadow="never">
      <el-table :data="tableData" v-loading="loading" style="width: 100%" class="custom-table" @sort-change="handleSortChange">
        <el-table-column prop="id" label="ID" width="70" align="center"></el-table-column>
        <el-table-column prop="title" label="客诉标题" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column prop="complaintType" label="客诉类型" width="120" align="center">
          <template slot-scope="scope">
            <span>{{ getComplaintTypeLabel(scope.row.complaintType) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="productUuid" label="关联产品" width="140" show-overflow-tooltip></el-table-column>
        <el-table-column prop="userId" label="用户ID" width="80" align="center"></el-table-column>
        <el-table-column prop="followStatus" label="跟进状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.followStatus)" size="small">{{ getStatusLabel(scope.row.followStatus) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="handlerName" label="处理人" width="100" align="center">
          <template slot-scope="scope">
            {{ scope.row.handlerName || scope.row.handlerId || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="submitDate" label="提交日期" width="160" align="center" sortable="custom">
          <template slot-scope="scope">
            {{ formatDate(scope.row.submitDate) }}
          </template>
        </el-table-column>
        <el-table-column v-if="hasAnyActionPermission" :label="$t('common.operation')" :width="actionColumnWidth" align="center" fixed="right">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button type="primary" size="mini" plain icon="el-icon-view" :aria-label="$t('common.detail')" @click="handleView(scope.row)" v-if="hasPermission('cus:consultation:info')">{{ $t('common.detail') }}</el-button>
              <el-button type="warning" size="mini" plain icon="el-icon-edit" :aria-label="$t('common.edit')" @click="handleEdit(scope.row)" v-if="hasPermission('cus:consultation:update')">{{ $t('common.edit') }}</el-button>
              <el-button class="btn-action-status" size="mini" plain icon="el-icon-setting" :aria-label="$t('common.status')" @click="openStatusDialog(scope.row)" v-if="hasPermission('cus:consultation:update')">{{ $t('common.status') }}</el-button>
              <el-button type="danger" size="mini" plain icon="el-icon-delete" :aria-label="$t('common.delete')" @click="handleDelete(scope.row)" v-if="hasPermission('cus:consultation:delete')">{{ $t('common.delete') }}</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="limit"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
        ></el-pagination>
      </div>
    </el-card>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="700px" :close-on-click-modal="false" @close="resetForm">
      <el-form ref="consultationForm" :model="formData" :rules="formRules" label-width="100px">
        <el-form-item label="客诉标题" prop="title">
          <el-input v-model="formData.title" placeholder="请输入客诉标题" maxlength="200"></el-input>
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="客诉类型" prop="complaintType">
              <el-select v-model="formData.complaintType" placeholder="请选择客诉类型" style="width: 100%">
                <el-option v-for="item in complaintTypeList" :key="item.key" :label="item.name" :value="item.key"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="关联产品" prop="productUuid">
              <el-input v-model="formData.productUuid" placeholder="请输入产品UUID"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="用户ID" prop="userId">
              <el-input v-model.number="formData.userId" placeholder="请输入用户ID"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系方式" prop="contactInfo">
              <el-input v-model="formData.contactInfo" placeholder="手机号/邮箱等"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="客诉详情" prop="description">
          <el-input v-model="formData.description" type="textarea" :rows="4" placeholder="请输入客诉详情描述"></el-input>
        </el-form-item>
        <el-form-item label="附件" prop="attachmentUrls">
          <div class="attachment-upload-area">
            <el-button size="small" type="primary" icon="el-icon-upload2" @click="triggerAttachmentUpload">上传图片</el-button>
            <span class="upload-tip">支持多选，单文件不超过10MB</span>
          </div>
          <input type="file" ref="attachmentInput" style="display: none" accept="image/*" multiple @change="handleAttachmentUpload" />
          <div class="attachment-preview" v-if="parseAttachmentUrls(formData.attachmentUrls).length > 0">
            <div class="attachment-item" v-for="(url, idx) in parseAttachmentUrls(formData.attachmentUrls)" :key="idx">
              <el-image v-if="isImageUrl(url)" :src="resolveUrl(url)" fit="cover" :preview-src-list="getImageUrls(formData.attachmentUrls)" style="width: 60px; height: 60px; border-radius: 4px;"></el-image>
              <el-link v-else :href="resolveUrl(url)" target="_blank" type="primary" :underline="false" style="font-size: 12px;">{{ getFileName(url) }}</el-link>
              <i class="el-icon-close attachment-remove" @click="removeAttachment(idx)"></i>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="formData.remark" type="textarea" :rows="2" placeholder="备注信息"></el-input>
        </el-form-item>
        <template v-if="isEdit">
          <el-divider content-position="left">处理信息</el-divider>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="跟进状态" prop="followStatus">
                <el-select v-model="formData.followStatus" placeholder="请选择状态" style="width: 100%">
                  <el-option label="未处理" value="PENDING"></el-option>
                  <el-option label="处理中" value="IN_PROGRESS"></el-option>
                  <el-option label="已解决" value="RESOLVED"></el-option>
                  <el-option label="已关闭" value="CLOSED"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="处理人">
                <el-input :value="formData.handlerName || formData.handlerId || '待分配'" disabled></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="处理结果" prop="handleResult">
            <el-input v-model="formData.handleResult" type="textarea" :rows="3" placeholder="请输入处理结果"></el-input>
          </el-form-item>
        </template>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确定</el-button>
      </div>
    </el-dialog>

    <el-dialog title="客诉详情" :visible.sync="detailVisible" width="750px">
      <el-descriptions :column="2" border size="medium" v-if="detailData">
        <el-descriptions-item label="ID">{{ detailData.id }}</el-descriptions-item>
        <el-descriptions-item label="客诉标题">{{ detailData.title }}</el-descriptions-item>
        <el-descriptions-item label="客诉类型">{{ getComplaintTypeLabel(detailData.complaintType) }}</el-descriptions-item>
        <el-descriptions-item label="跟进状态">
          <el-tag :type="getStatusType(detailData.followStatus)" size="small">{{ getStatusLabel(detailData.followStatus) }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="关联产品">{{ detailData.productUuid || '-' }}</el-descriptions-item>
        <el-descriptions-item label="用户ID">{{ detailData.userId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="联系方式">{{ detailData.contactInfo || '-' }}</el-descriptions-item>
        <el-descriptions-item label="提交日期">{{ formatDate(detailData.submitDate) }}</el-descriptions-item>
        <el-descriptions-item label="处理人">{{ detailData.handlerName || detailData.handlerId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="处理时间">{{ formatDate(detailData.handleTime) }}</el-descriptions-item>
        <el-descriptions-item label="客诉详情" :span="2">{{ detailData.description || '-' }}</el-descriptions-item>
        <el-descriptions-item label="处理结果" :span="2">{{ detailData.handleResult || '-' }}</el-descriptions-item>
        <el-descriptions-item label="附件" :span="2">
          <template v-if="detailData.attachmentUrls && parseAttachmentUrls(detailData.attachmentUrls).length > 0">
            <div class="detail-attachments">
              <div v-for="(url, idx) in parseAttachmentUrls(detailData.attachmentUrls)" :key="idx" class="detail-attachment-item">
                <el-image
                  v-if="isImageUrl(url)"
                  :src="resolveUrl(url)"
                  fit="cover"
                  :preview-src-list="getImageUrls(detailData.attachmentUrls)"
                  style="width: 100px; height: 100px; border-radius: 4px; border: 1px solid #ebeef5;"
                ></el-image>
                <el-link v-else :href="resolveUrl(url)" target="_blank" type="primary" :underline="false">
                  <i class="el-icon-link"></i> {{ getFileName(url) }}
                </el-link>
              </div>
            </div>
          </template>
          <span v-else>-</span>
        </el-descriptions-item>
        <el-descriptions-item label="备注" :span="2">{{ detailData.remark || '-' }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDate(detailData.createDate) }}</el-descriptions-item>
        <el-descriptions-item label="更新时间">{{ formatDate(detailData.updateDate) }}</el-descriptions-item>
      </el-descriptions>
      <el-divider content-position="left">状态变更记录</el-divider>
      <div class="status-log-container" v-loading="logLoading">
        <el-timeline v-if="statusLogs.length > 0">
          <el-timeline-item
            v-for="log in statusLogs"
            :key="log.id"
            :timestamp="formatDate(log.createDate)"
            placement="top"
            :type="getLogTimelineType(log.action)"
          >
            <div class="log-item">
              <div class="log-header">
                <span class="log-operator">{{ log.username || '系统' }}</span>
                <el-tag size="mini" :type="getLogTagType(log.action)">{{ getLogActionLabel(log.action) }}</el-tag>
              </div>
              <div class="log-detail" v-if="log.detail">{{ log.detail }}</div>
            </div>
          </el-timeline-item>
        </el-timeline>
        <el-empty v-else description="暂无状态变更记录" :image-size="60"></el-empty>
      </div>
      <div slot="footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog title="变更客诉状态" :visible.sync="statusDialogVisible" width="680px" :close-on-click-modal="false">
      <div class="status-change-info" v-if="statusChangeRow">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="客诉标题" :span="2">{{ statusChangeRow.title }}</el-descriptions-item>
          <el-descriptions-item label="客诉类型">{{ getComplaintTypeLabel(statusChangeRow.complaintType) }}</el-descriptions-item>
          <el-descriptions-item label="当前状态">
            <el-tag :type="getStatusType(statusChangeRow.followStatus)" size="small">{{ getStatusLabel(statusChangeRow.followStatus) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="提交时间">{{ formatDate(statusChangeRow.submitDate) || '-' }}</el-descriptions-item>
          <el-descriptions-item label="联系方式">{{ statusChangeRow.contactInfo || '-' }}</el-descriptions-item>
          <el-descriptions-item label="关联产品" :span="2">{{ statusChangeRow.productUuid || '-' }}</el-descriptions-item>
          <el-descriptions-item label="客诉详情" :span="2">{{ statusChangeRow.description || '-' }}</el-descriptions-item>
          <el-descriptions-item label="处理人" v-if="statusChangeRow.handlerId">{{ statusChangeRow.handlerName || statusChangeRow.handlerId }}</el-descriptions-item>
          <el-descriptions-item label="处理时间" v-if="statusChangeRow.handleTime">{{ formatDate(statusChangeRow.handleTime) }}</el-descriptions-item>
        </el-descriptions>
      </div>
      <el-form ref="statusForm" :model="statusFormData" :rules="statusFormRules" label-width="100px" style="margin-top: 16px;">
        <el-form-item label="目标状态" prop="targetStatus">
          <el-select v-model="statusFormData.targetStatus" placeholder="请选择目标状态" style="width: 100%" @change="handleTargetStatusChange">
            <el-option
              v-for="opt in availableStatusOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="处理结果" prop="handleResult" v-if="statusFormData.targetStatus === 'RESOLVED'">
          <el-input v-model="statusFormData.handleResult" type="textarea" :rows="4" placeholder="请输入客诉处理过程和结果"></el-input>
        </el-form-item>
        <el-form-item label="变更备注" prop="remark">
          <el-input v-model="statusFormData.remark" type="textarea" :rows="3" placeholder="请输入状态变更原因或备注信息"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="statusDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleStatusChange" :loading="statusLoading">确认变更</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getConsultationPage,
  getConsultationDetail,
  createConsultation,
  updateConsultation,
  deleteConsultation,
  updateConsultationStatus,
  getConsultationStatistics,
  getConsultationLogs,
  uploadConsultationFile
} from '@/apis/cus/consultation'
import { getDictDataByType } from '@/apis/system/dict'
import { hasPermission } from '@/utils/permission'
import { getFullUrl } from '@/utils'

const STATUS_FLOW = {
  PENDING: ['IN_PROGRESS', 'RESOLVED', 'CLOSED'],
  IN_PROGRESS: ['RESOLVED', 'CLOSED'],
  RESOLVED: ['CLOSED'],
  CLOSED: []
}

export default {
  name: 'ConsultationList',
  data() {
    return {
      loading: false,
      submitLoading: false,
      tableData: [],
      page: 1,
      limit: 10,
      total: 0,
      searchForm: {
        title: '',
        followStatus: '',
        complaintType: '',
        dateRange: null
      },
      statistics: null,
      complaintTypeList: [],
      dialogVisible: false,
      detailVisible: false,
      isEdit: false,
      formData: {
        id: null,
        title: '',
        complaintType: '',
        productUuid: '',
        userId: null,
        contactInfo: '',
        description: '',
        followStatus: 'PENDING',
        handlerId: null,
        handleResult: '',
        attachmentUrls: null,
        remark: ''
      },
      detailData: null,
      formRules: {
        title: [{ required: true, message: '请输入客诉标题', trigger: 'blur' }]
      },
      statCards: {
        total: { label: '总计', class: 'stat-total' },
        pending: { label: '未处理', class: 'stat-pending' },
        inProgress: { label: '处理中', class: 'stat-in-progress' },
        resolved: { label: '已解决', class: 'stat-resolved' },
        closed: { label: '已关闭', class: 'stat-closed' }
      },
      statusDialogVisible: false,
      statusChangeRow: null,
      statusFormData: {
        targetStatus: '',
        remark: '',
        handleResult: ''
      },
      statusFormRules: {
        targetStatus: [{ required: true, message: '请选择目标状态', trigger: 'change' }],
        handleResult: [{ required: true, message: '请输入处理结果', trigger: 'blur' }]
      },
      statusLoading: false,
      statusLogs: [],
      logLoading: false
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑客诉' : '新建客诉'
    },
    availableStatusOptions() {
      if (!this.statusChangeRow) return []
      const current = this.statusChangeRow.followStatus
      const allowed = STATUS_FLOW[current] || []
      return [
        { value: 'IN_PROGRESS', label: '处理中' },
        { value: 'RESOLVED', label: '已解决' },
        { value: 'CLOSED', label: '已关闭' }
      ].filter(opt => allowed.includes(opt.value))
    },
    visibleActionCount() {
      let count = 0
      if (this.hasPermission('cus:consultation:info')) count += 1
      if (this.hasPermission('cus:consultation:update')) count += 2
      if (this.hasPermission('cus:consultation:delete')) count += 1
      return count
    },
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 100, 2: 170, 3: 245, 4: 300 }
      return widthMap[count] || 0
    }
  },
  created() {
    this.loadComplaintTypes()
    this.loadData()
    this.loadStatistics()
  },
  methods: {
    hasPermission,
    resolveUrl(url) {
      return getFullUrl(url)
    },
    async loadData() {
      this.loading = true
      try {
        const params = {
          page: this.page,
          limit: this.limit
        }
        if (this.searchForm.title) params.title = this.searchForm.title
        if (this.searchForm.followStatus) params.followStatus = this.searchForm.followStatus
        if (this.searchForm.complaintType) params.complaintType = this.searchForm.complaintType
        if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
          params.startDate = this.searchForm.dateRange[0]
          params.endDate = this.searchForm.dateRange[1]
        }
        const res = await getConsultationPage(params)
        if (res) {
          this.tableData = res.list || []
          this.total = res.total || 0
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async loadStatistics() {
      try {
        const params = {}
        if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
          params.startDate = this.searchForm.dateRange[0]
          params.endDate = this.searchForm.dateRange[1]
        }
        const res = await getConsultationStatistics(params)
        if (res) {
          this.statistics = res
        }
      } catch (e) {
        console.error(e)
      }
    },
    async loadComplaintTypes() {
      try {
        const res = await getDictDataByType('COMPLAINT_TYPE')
        this.complaintTypeList = res || []
      } catch (e) {
        console.error('加载客诉类型失败:', e)
      }
    },
    async loadStatusLogs(targetId) {
      this.logLoading = true
      try {
        const res = await getConsultationLogs(targetId)
        if (res) {
          this.statusLogs = res.list || []
        }
      } catch (e) {
        console.error('加载状态变更记录失败:', e)
        this.statusLogs = []
      } finally {
        this.logLoading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.loadData()
      this.loadStatistics()
    },
    handleReset() {
      this.searchForm = { title: '', followStatus: '', complaintType: '', dateRange: null }
      this.page = 1
      this.loadData()
      this.loadStatistics()
    },
    handleSizeChange(val) {
      this.limit = val
      this.page = 1
      this.loadData()
    },
    handleCurrentChange(val) {
      this.page = val
      this.loadData()
    },
    handleSortChange({ prop, order }) {
      this.loadData()
    },
    handleAdd() {
      this.isEdit = false
      this.formData = {
        id: null, title: '', complaintType: '', productUuid: '', userId: null,
        contactInfo: '', description: '', followStatus: 'PENDING', handlerId: null,
        handleResult: '', attachmentUrls: null, remark: ''
      }
      this.dialogVisible = true
    },
    async handleEdit(row) {
      this.isEdit = true
      try {
        const res = await getConsultationDetail(row.id)
        if (res) {
          this.formData = { ...res }
          this.dialogVisible = true
        }
      } catch (e) {
        this.$message.error('获取客诉详情失败')
      }
    },
    async handleView(row) {
      try {
        const res = await getConsultationDetail(row.id)
        if (res) {
          this.detailData = res
          this.detailVisible = true
          this.loadStatusLogs(row.id)
        }
      } catch (e) {
        this.$message.error('获取客诉详情失败')
      }
    },
    handleDelete(row) {
      this.$confirm('确定要删除该客诉记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await deleteConsultation([row.id])
          if (res !== undefined) {
            this.$message.success('删除成功')
            this.loadData()
            this.loadStatistics()
          } else {
            this.$message.error('删除失败')
          }
        } catch (e) {
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },
    openStatusDialog(row) {
      this.statusChangeRow = row
      this.statusFormData = { targetStatus: '', remark: '', handleResult: '' }
      this.statusDialogVisible = true
      this.$nextTick(() => {
        this.$refs.statusForm && this.$refs.statusForm.clearValidate()
      })
    },
    handleTargetStatusChange(val) {
      if (val !== 'RESOLVED') {
        this.statusFormData.handleResult = ''
      }
      this.$nextTick(() => {
        this.$refs.statusForm && this.$refs.statusForm.clearValidate('handleResult')
      })
    },
    handleStatusChange() {
      this.$refs.statusForm.validate(async (valid) => {
        if (!valid) return
        this.statusLoading = true
        try {
          await updateConsultationStatus(
            this.statusChangeRow.id,
            this.statusFormData.targetStatus,
            this.statusFormData.remark,
            this.statusFormData.handleResult
          )
          this.$message.success('状态变更成功')
          this.statusDialogVisible = false
          this.loadData()
          this.loadStatistics()
        } catch (e) {
          this.$message.error('状态变更失败')
        } finally {
          this.statusLoading = false
        }
      })
    },
    async handleSubmit() {
      this.$refs.consultationForm.validate(async (valid) => {
        if (!valid) return
        this.submitLoading = true
        try {
          const api = this.isEdit ? updateConsultation : createConsultation
          await api(this.formData)
          this.$message.success(this.isEdit ? '更新成功' : '创建成功')
          this.dialogVisible = false
          this.loadData()
          this.loadStatistics()
        } catch (e) {
          this.$message.error('操作失败')
        } finally {
          this.submitLoading = false
        }
      })
    },
    resetForm() {
      this.$refs.consultationForm && this.$refs.consultationForm.resetFields()
    },
    getStatusLabel(status) {
      const map = { PENDING: '未处理', IN_PROGRESS: '处理中', RESOLVED: '已解决', CLOSED: '已关闭' }
      return map[status] || status
    },
    getStatusType(status) {
      const map = { PENDING: 'info', IN_PROGRESS: 'warning', RESOLVED: 'success', CLOSED: '' }
      return map[status] || 'info'
    },
    getComplaintTypeLabel(value) {
      const item = this.complaintTypeList.find(i => i.key === value)
      return item ? item.name : (value || '-')
    },
    getLogTimelineType(action) {
      if (action === 'STATUS_CHANGE') return 'warning'
      if (action === '创建客诉') return 'primary'
      if (action === '更新客诉') return ''
      if (action === '删除客诉') return 'danger'
      return 'primary'
    },
    getLogTagType(action) {
      if (action === 'STATUS_CHANGE') return 'warning'
      if (action === '创建客诉') return 'success'
      if (action === '更新客诉') return ''
      if (action === '删除客诉') return 'danger'
      return 'info'
    },
    getLogActionLabel(action) {
      if (action === 'STATUS_CHANGE') return '状态变更'
      return action || '操作'
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') + ' ' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')
    },
    parseAttachmentUrls(urls) {
      if (!urls) return []
      if (Array.isArray(urls)) return urls
      try {
        const parsed = JSON.parse(urls)
        if (Array.isArray(parsed)) return parsed
      } catch (e) {
        if (typeof urls === 'string' && urls.trim().length > 0) {
          return urls.split(',').map(u => u.trim()).filter(u => u.length > 0)
        }
      }
      return []
    },
    getImageUrls(urls) {
      return this.parseAttachmentUrls(urls).filter(u => this.isImageUrl(u)).map(u => this.resolveUrl(u))
    },
    isImageUrl(url) {
      if (!url) return false
      const lower = url.toLowerCase()
      return lower.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i) !== null || lower.includes('/image') || lower.includes('/img')
    },
    getFileName(url) {
      if (!url) return ''
      try {
        const parts = url.split('/')
        const name = parts[parts.length - 1]
        return name.length > 20 ? name.substring(0, 20) + '...' : name
      } catch (e) {
        return url
      }
    },
    triggerAttachmentUpload() {
      this.$refs.attachmentInput.click()
    },
    async handleAttachmentUpload(e) {
      const files = e.target.files
      if (!files || files.length === 0) return
      const maxSize = 10 * 1024 * 1024
      const currentUrls = this.parseAttachmentUrls(this.formData.attachmentUrls)
      let successCount = 0
      let failCount = 0
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (file.size > maxSize) {
          this.$message.warning(`${file.name} 超过10MB，已跳过`)
          failCount++
          continue
        }
        try {
          const fd = new FormData()
          fd.append('file', file)
          const res = await uploadConsultationFile(fd)
          if (res) {
            currentUrls.push(res)
            successCount++
          } else {
            failCount++
          }
        } catch (err) {
          failCount++
        }
      }
      this.formData.attachmentUrls = JSON.stringify(currentUrls)
      if (successCount > 0) {
        this.$message.success(`成功上传 ${successCount} 个文件`)
      }
      if (failCount > 0) {
        this.$message.warning(`${failCount} 个文件上传失败`)
      }
      e.target.value = ''
    },
    removeAttachment(idx) {
      const urls = this.parseAttachmentUrls(this.formData.attachmentUrls)
      urls.splice(idx, 1)
      this.formData.attachmentUrls = urls.length > 0 ? JSON.stringify(urls) : null
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/list-page.scss";

/* 客诉管理页面特有样式 */
.list-page-container {
  .statistics-panel {
    display: flex;
    gap: 16px;
    margin-top: 10px;
  }
  .stat-card {
    flex: 1;
    background: #fff;
    border-radius: 4px;
    padding: 16px;
    text-align: center;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  }
  .stat-value {
    font-size: 28px;
    font-weight: 600;
    color: #303133;
  }
  .stat-label {
    font-size: 13px;
    color: #909399;
    margin-top: 4px;
  }
  .stat-pending .stat-value { color: #909399; }
  .stat-in-progress .stat-value { color: #e6a23c; }
  .stat-resolved .stat-value { color: #67c23a; }
  .stat-closed .stat-value { color: #409eff; }
  .delete-btn {
    color: #f56c6c !important;
  }
  .status-log-container {
    max-height: 300px;
    overflow-y: auto;
    padding: 0 8px;
  }
  .log-item {
    padding: 4px 0;
  }
  .log-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }
  .log-operator {
    font-weight: 500;
    color: #303133;
    font-size: 14px;
  }
  .log-detail {
    color: #606266;
    font-size: 13px;
    line-height: 1.5;
  }
  .status-change-info {
    margin-bottom: 8px;
  }
  .attachment-upload-area {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .upload-tip {
    font-size: 12px;
    color: #909399;
  }
  .attachment-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }
  .attachment-item {
    position: relative;
    display: inline-block;
  }
  .attachment-remove {
    position: absolute;
    top: -6px;
    right: -6px;
    background: #f56c6c;
    color: #fff;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    line-height: 18px;
    text-align: center;
    font-size: 12px;
    cursor: pointer;
    z-index: 1;
  }
  .detail-attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .detail-attachment-item {
    display: inline-block;
  }
}
</style>
